import "@aws-amplify/ui-react/styles.css";
import { Container, Flex, MantineProvider, Paper } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { APIProvider } from "@vis.gl/react-google-maps";
import { fetchAuthSession } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { useCallback, useEffect, useState } from "react";
import { Redirect, useRoute } from "wouter";
import { displayNotification, FCMManager, initFCMPassive } from "@/fcm";
import { idTokenToUserInfo, type JwtToken, UserContext } from "@/UserContext";
import { isPushNotificationEnabled } from "@/utils/pushSettings";
import { BottomNavigationBar, TopNavigationBar } from "./components/NavBars";
import TestRealmIndicator from "./components/TestRealmIndicator";
import { config } from "./config";
import { NotificationsProvider } from "./hooks/useNotifications";
import { useTestRealm } from "./hooks/useTestRealm";
import {
  OwnerNavigationIcons,
  OwnerNavigationIconsDesktop,
  OwnerRouter,
} from "./routers/OwnerRouter";
import { UniversalRoutes } from "./routers/routes";
import {
  TrainerNavigationIcons,
  TrainerNavigationIconsDesktop,
  TrainerRouter,
} from "./routers/TrainerRouter";
import { theme } from "./theme";
import { WoofapyAuthenticator } from "./WoofapyAuthenticator";
import { useWebSocket, WebSocketProvider } from "./websocket/client";

const DesktopContentWithNavigation = ({
  children,
  role,
}: {
  children: React.ReactNode;
  role: string | undefined;
}) => {
  const navigationIcons =
    role === "owner"
      ? OwnerNavigationIconsDesktop
      : TrainerNavigationIconsDesktop;

  return (
    <Flex bg="#fafafa" mih="100vh" direction="column">
      <TopNavigationBar icons={navigationIcons} />
      <Container
        size="1200px"
        style={{
          flex: 1,
          maxWidth: "800px",
          width: "100%",
          margin: "0 auto",
        }}
        pb="2em"
        pt="2em"
        px="lg"
      >
        <Paper withBorder p="xl">
          {children}
        </Paper>
      </Container>
    </Flex>
  );
};

const MobileContentWithNavigation = ({
  children,
  role,
}: {
  children: React.ReactNode;
  role: string | undefined;
}) => {
  const navigationIcons =
    role === "owner" ? OwnerNavigationIcons : TrainerNavigationIcons;

  return (
    <Flex bg="white" mih="100vh">
      <Container size="440px" style={{ flex: 1 }} pb="80px" pt="1em">
        {children}
        <BottomNavigationBar icons={navigationIcons} />
      </Container>
    </Flex>
  );
};

const AuthenticatedApp = () => {
  const [idToken, setIdToken] = useState<JwtToken | undefined>();
  const userInfo = idToken && idTokenToUserInfo(idToken.payload);
  const { setAuthToken, lastMessage } = useWebSocket();
  const isTestRealmActive = useTestRealm();

  useEffect(() => {
    if (idToken && userInfo?.isProfileCreated) {
      // This is for websocket authentication
      setAuthToken(idToken.toString());
    }
  }, [idToken, setAuthToken, userInfo?.isProfileCreated]);

  useEffect(() => {
    if (!userInfo) {
      return;
    }
    if (
      lastMessage &&
      lastMessage.type === "notification" &&
      lastMessage.notification.type !== "CHAT_MESSAGE" &&
      isPushNotificationEnabled(lastMessage.notification.type)
    ) {
      displayNotification({
        ...lastMessage.notification,
        role: userInfo.role,
      });
    }
  }, [lastMessage, userInfo]);

  const updateUserInfo = useCallback(async () => {
    const session = await fetchAuthSession();
    setIdToken(session.tokens?.idToken);
  }, []);

  const role = userInfo?.role;

  useEffect(() => {
    if (role === "trainer") {
      document.title = "Woofapy for trainers";
    }
    if (role === "owner") {
      document.title = "Woofapy";
    }
  }, [role]);

  useEffect(() => {
    Hub.listen("auth", async ({ payload }) => {
      switch (payload.event) {
        case "signedIn":
          await updateUserInfo();
          await initFCMPassive();
          break;
        case "signedOut":
          await FCMManager.revoke();
          break;
        case "tokenRefresh":
          await updateUserInfo();
          break;
      }
    });
    updateUserInfo();
    initFCMPassive();
  }, [updateUserInfo]);

  const [onOwnerProfilePage] = useRoute(UniversalRoutes.PROFILE.CREATE);
  const [onTrainerProfilePage] = useRoute(UniversalRoutes.PROFILE.CREATE);
  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (!userInfo) {
    return null;
  }

  if (!userInfo.isProfileCreated) {
    if (role === "trainer" && !onTrainerProfilePage) {
      return <Redirect to={UniversalRoutes.PROFILE.CREATE} />;
    } else if (role === "owner" && !onOwnerProfilePage) {
      return <Redirect to={UniversalRoutes.PROFILE.CREATE} />;
    }
  }

  const mainContent = (
    <>
      {isTestRealmActive && <TestRealmIndicator />}
      {role === "owner" && <OwnerRouter />}
      {role === "trainer" && <TrainerRouter />}
    </>
  );

  return (
    <APIProvider
      apiKey={config.googleMapsApiKey}
      solutionChannel="GMP_devsite_samples_v3_rgmautocomplete"
    >
      <UserContext.Provider value={userInfo}>
        <NotificationsProvider>
          <Notifications position="top-center" limit={3} />
          {isDesktop ? (
            <DesktopContentWithNavigation role={role}>
              {mainContent}
            </DesktopContentWithNavigation>
          ) : (
            <MobileContentWithNavigation role={role}>
              {mainContent}
            </MobileContentWithNavigation>
          )}
        </NotificationsProvider>
      </UserContext.Provider>
    </APIProvider>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MantineProvider theme={theme}>
        <ModalsProvider>
          <WoofapyAuthenticator>
            <WebSocketProvider baseUrl={config.wsBaseUrl}>
              <AuthenticatedApp />
            </WebSocketProvider>
          </WoofapyAuthenticator>
        </ModalsProvider>
      </MantineProvider>
    </QueryClientProvider>
  );
}
